// Outline panel — a click-to-scroll list of headings extracted from
// the active note. Renders in the reader's right gutter on wide
// viewports; hides on narrow ones.
//
// The slug logic in lib/notes-markdown matches the heading renderer
// so getElementById(slug) lands on the right node. Headings inside
// fenced code blocks are skipped at extraction time.

import { useMemo } from "preact/hooks";
import { extractOutline } from "../../lib/notes-markdown";

interface OutlineProps {
  content: string;
  scrollContainer: HTMLElement | null;
}

export function NotesOutline({ content, scrollContainer }: OutlineProps) {
  const headings = useMemo(() => extractOutline(content), [content]);
  if (headings.length === 0) return null;

  const onClick = (slug: string) => {
    if (!scrollContainer) return;
    const el = scrollContainer.querySelector<HTMLElement>(`#${cssEscape(slug)}`);
    if (!el) return;
    // The reader body owns its own scroll, so scrollIntoView with the
    // wrong container would scroll the page. Compute offset manually.
    const top = el.offsetTop - scrollContainer.offsetTop - 8;
    scrollContainer.scrollTo({ top, behavior: "smooth" });
  };

  return (
    <aside className="notes-outline" aria-label="Outline">
      <div className="notes-outline-head">Outline</div>
      <ul className="notes-outline-list">
        {headings.map((h, i) => (
          <li
            key={`${h.slug}-${i}`}
            className="notes-outline-item"
            data-depth={Math.min(h.depth, 4)}
          >
            <button type="button" onClick={() => onClick(h.slug)} title={h.text}>
              {h.text}
            </button>
          </li>
        ))}
      </ul>
    </aside>
  );
}

// CSS.escape isn't on the type definitions everywhere, and slugs from
// notes-markdown only contain [a-z0-9-], so a manual escape that just
// hexes anything outside that range is enough.
function cssEscape(slug: string): string {
  if (typeof (window as any).CSS?.escape === "function") return (window as any).CSS.escape(slug);
  return slug.replace(/[^a-z0-9_-]/gi, (c) => `\\${c.charCodeAt(0).toString(16)} `);
}
